import { useState } from "react";
import { useAuth } from "../lib/auth";
import { useChild } from "../lib/childContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import ChildSwitcher from "../components/ChildSwitcher";
import AmbientDot from "../components/AmbientDot";
import { Plus } from "lucide-react";

const SAMPLE_PORTRAIT =
  "https://static.prod-images.emergentagent.com/jobs/5b7539fb-c408-4336-acd4-f414390d8b8e/images/e81fd853713c8fa600e57f71b731ee4e0da43ef25511d6a4b5bc30ffc058b899.png";

function currentStateText(s) {
  return {
    arrived: "has just arrived",
    playing: "is playing",
    resting: "is resting",
    eating: "is having a snack",
    learning: "is exploring",
    safe: "is held",
  }[s];
}

export default function ChildProfile() {
  const { user } = useAuth();
  const { selected, refresh, loading } = useChild();
  const [showAdd, setShowAdd] = useState(false);

  if (loading) {
    return (
      <Shell>
        <p className="font-serif italic text-[#6A6863]">a quiet moment…</p>
      </Shell>
    );
  }

  if (!selected) {
    return (
      <Shell>
        <div className="fade-up max-w-2xl">
          <span className="eyebrow">Child</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
            No one is held here yet.
          </h1>
          <p className="mt-8 text-lg text-[#6A6863] font-light">
            {user?.role === "parent"
              ? "When you're ready, add a child to begin."
              : "Wait gently. A parent will connect you when the time is right."}
          </p>
          {user?.role === "parent" && (
            <button
              onClick={() => setShowAdd(true)}
              data-testid="add-child-open"
              className="btn-primary mt-12 inline-flex"
            >
              Add a child
              <Plus size={16} strokeWidth={1.5} />
            </button>
          )}
        </div>
        {showAdd && (
          <AddChildModal
            onClose={() => setShowAdd(false)}
            onCreated={async () => {
              setShowAdd(false);
              await refresh();
              toast("A new thread has begun, gently.");
            }}
          />
        )}
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="fade-up">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <span className="eyebrow">Child</span>
          {user?.role === "parent" && (
            <button
              onClick={() => setShowAdd(true)}
              data-testid="add-another-child"
              className="btn-ghost !py-2 !px-4 !text-xs"
            >
              Add another
              <Plus size={14} strokeWidth={1.5} />
            </button>
          )}
        </div>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
          {selected.name},{" "}
          <em className="italic text-[#8A9984]">{selected.age}.</em>
        </h1>
      </div>

      <section className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div
            className="rounded-3xl overflow-hidden border hairline"
            style={{
              aspectRatio: "4/5",
              backgroundImage: `url(${selected.portrait_url || SAMPLE_PORTRAIT})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundColor: "#F2EFEA",
            }}
          />
          <div className="mt-6 flex items-center gap-3">
            <AmbientDot state={selected.current_state} size={10} />
            <span className="text-sm text-[#6A6863] font-light">
              {selected.name} {currentStateText(selected.current_state)}.
            </span>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-10">
          <Detail label="Comfort items">
            {selected.comfort_items.length ? (
              <ul className="mt-2 space-y-1">
                {selected.comfort_items.map((c) => (
                  <li key={c} className="font-serif text-xl italic text-[#2C2B29]">
                    · {c}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="font-serif italic text-[#6A6863]">—</p>
            )}
          </Detail>
          <Detail label="Gentle notes">
            {selected.allergies.length ? (
              <ul className="mt-2 space-y-1">
                {selected.allergies.map((a) => (
                  <li key={a} className="font-serif text-xl italic text-[#2C2B29]">
                    · {a}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="font-serif italic text-[#6A6863]">None recorded.</p>
            )}
          </Detail>
          <Detail label="Caregivers">
            {(selected.caregiver_ids || []).length === 0 && !selected.caregiver_id ? (
              <p className="font-serif italic text-[#6A6863]">Not yet connected</p>
            ) : (
              <p className="font-serif text-xl italic text-[#2C2B29]">
                {Math.max(
                  (selected.caregiver_ids || []).length,
                  selected.caregiver_id ? 1 : 0
                )}{" "}
                trusted{" "}
                {Math.max(
                  (selected.caregiver_ids || []).length,
                  selected.caregiver_id ? 1 : 0
                ) === 1
                  ? "adult"
                  : "adults"}{" "}
                connected
              </p>
            )}
          </Detail>
          <Detail label="Parents">
            <p className="font-serif text-xl italic text-[#2C2B29]">
              {selected.parent_ids.length === 1
                ? "One parent (you)"
                : `${selected.parent_ids.length} co-parents`}
            </p>
          </Detail>
        </div>
      </section>

      {showAdd && (
        <AddChildModal
          onClose={() => setShowAdd(false)}
          onCreated={async () => {
            setShowAdd(false);
            await refresh();
            toast("A new thread has begun, gently.");
          }}
        />
      )}
    </Shell>
  );
}

function Detail({ label, children }) {
  return (
    <div
      data-testid={`child-detail-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="eyebrow">{label}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function AddChildModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    age: "",
    allergies: "",
    comfort_items: "",
    caregiver_email: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/children", {
        name: form.name.trim(),
        age: form.age.trim(),
        allergies: form.allergies
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        comfort_items: form.comfort_items
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        caregiver_email: form.caregiver_email.trim() || undefined,
        portrait_url: SAMPLE_PORTRAIT,
      });
      onCreated();
    } catch (err) {
      toast(err.response?.data?.detail || "We couldn't add them just yet.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="add-child-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#FBF9F6]/85 backdrop-blur-md px-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg bg-white rounded-3xl border hairline p-8 sm:p-10 fade-up"
      >
        <span className="eyebrow">Add a child</span>
        <h2 className="mt-3 font-serif text-3xl tracking-tight">
          A new thread begins.
        </h2>
        <div className="mt-8 space-y-5">
          <input
            required
            placeholder="Name"
            value={form.name}
            onChange={set("name")}
            data-testid="add-child-name"
            className="orbit-input"
          />
          <input
            required
            placeholder="Age (e.g. 3 years)"
            value={form.age}
            onChange={set("age")}
            data-testid="add-child-age"
            className="orbit-input"
          />
          <input
            placeholder="Allergies (comma separated)"
            value={form.allergies}
            onChange={set("allergies")}
            data-testid="add-child-allergies"
            className="orbit-input"
          />
          <input
            placeholder="Comfort items (comma separated)"
            value={form.comfort_items}
            onChange={set("comfort_items")}
            data-testid="add-child-comfort"
            className="orbit-input"
          />
          <input
            placeholder="Caregiver email (optional)"
            value={form.caregiver_email}
            onChange={set("caregiver_email")}
            data-testid="add-child-caregiver"
            className="orbit-input"
          />
        </div>
        <div className="mt-10 flex items-center gap-3 flex-wrap">
          <button
            type="submit"
            disabled={busy}
            data-testid="add-child-submit"
            className="btn-primary"
          >
            {busy ? "Holding…" : "Add gently"}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="add-child-cancel"
            className="btn-ghost"
          >
            Not now
          </button>
        </div>
      </form>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#FBF9F6]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-4">
          <ChildSwitcher />
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-5 sm:px-10 pt-6 pb-12">{children}</main>
    </div>
  );
}
